/**
 * InstagramPostEmbed.tsx
 * Embeds a single Instagram post via Instagram's official embed iframe.
 * Includes a timeout-based fallback so visitors never see a broken embed.
 */
import { useState, useCallback, useEffect } from "react";
import { useDarkMode } from "@/context/DarkModeContext";

interface InstagramPostEmbedProps {
  postUrl: string;
  caption?: string;
  maxWidth?: number;
  className?: string;
}

function extractShortcode(url: string): string | null {
  const match = url.match(/instagram\.com\/(?:[^/]+\/)?(?:p|reel|tv)\/([^/?#]+)/);
  return match?.[1] ?? null;
}

export default function InstagramPostEmbed({
  postUrl,
  caption,
  maxWidth = 400,
  className = "",
}: InstagramPostEmbedProps) {
  const { isDark } = useDarkMode();
  const [loaded, setLoaded] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const shortcode = extractShortcode(postUrl);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!loaded) setTimedOut(true);
    }, 5000);
    return () => clearTimeout(t);
  }, [loaded]);

  const handleLoad = useCallback(() => {
    setLoaded(true);
    setTimedOut(false);
  }, []);

  if (!shortcode) {
    return (
      <div className={`rounded-lg border border-red-200 bg-red-50 p-4 text-center ${className}`}>
        <p className="text-sm text-red-600">Invalid Instagram URL</p>
        <p className="text-xs text-red-400 mt-1">{postUrl}</p>
      </div>
    );
  }

  const embedUrl = `https://www.instagram.com/p/${shortcode}/embed`;
  const viewUrl = `https://www.instagram.com/p/${shortcode}/`;

  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100";
  const footerBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100";

  return (
    <div
      className={`relative rounded-lg overflow-hidden shadow-sm ${cardBg} ${className}`}
      style={{ maxWidth }}
    >
      {/* Iframe — hidden until loaded, or hidden after timeout */}
      {!timedOut && (
        <iframe
          src={embedUrl}
          className="w-full border-0"
          style={{ minHeight: 480, opacity: loaded ? 1 : 0, position: loaded ? "relative" : "absolute", pointerEvents: loaded ? "auto" : "none" }}
          onLoad={handleLoad}
          scrolling="no"
          allowTransparency
          referrerPolicy="no-referrer"
          title={`Instagram post ${shortcode}`}
        />
      )}

      {/* Loading spinner */}
      {!loaded && !timedOut && (
        <div className="flex items-center justify-center bg-gray-50" style={{ minHeight: 300 }}>
          <div className="flex flex-col items-center gap-2">
            <div className="w-6 h-6 border-2 border-pink-400 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-xs text-gray-400">Loading post...</span>
          </div>
        </div>
      )}

      {/* Fallback card when embed fails or times out */}
      {timedOut && (
        <div className={`flex flex-col items-center justify-center p-6 gap-3 ${isDark ? "bg-gray-800" : "bg-gray-50"}`} style={{ minHeight: 300 }}>
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-pink-100">
            <i className="ri-instagram-line text-pink-500 text-xl"></i>
          </div>
          <p className={`text-sm font-semibold ${isDark ? "text-gray-300" : "text-gray-700"}`}>Instagram Post</p>
          <p className={`text-xs text-center max-w-[200px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            This post couldn&apos;t be previewed here. Tap below to view it on Instagram.
          </p>
        </div>
      )}

      {/* Footer — always visible */}
      <div className={`border-t p-3 flex items-center justify-between ${footerBg}`}>
        <div className="min-w-0">
          {caption ? (
            <p className={`text-xs truncate ${isDark ? "text-gray-400" : "text-gray-600"}`}>{caption}</p>
          ) : (
            <p className={`text-[10px] uppercase tracking-wide font-semibold ${isDark ? "text-gray-500" : "text-gray-400"}`}>Instagram</p>
          )}
        </div>
        <a
          href={viewUrl}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="flex items-center gap-1 text-xs font-semibold text-pink-500 hover:text-pink-700 transition-colors whitespace-nowrap flex-shrink-0 ml-2"
        >
          <i className="ri-external-link-line"></i>
          View on Instagram
        </a>
      </div>
    </div>
  );
}